import { useMutation } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  AssignStaffToPracticeRef,
  Staff,
  StaffSpeciality,
} from '@/types/settings-api';

/**
 * User-facing inputs the wizard / settings forms actually collect. The
 * mapper layers in the curl-verified legacy-compat fields.
 */
export interface CreateStaffInput {
  organizationId: string;
  firstName: string;
  lastName: string;
  emailId: string;
  /** Combined country-dial-then-digits wire form, e.g. `"+971-554654645"`. */
  mobileNumber: string;
  /** Numeric `TenantRole.roleId` — comes from `useRolesByType(2, orgId)`. */
  role: number;
  /**
   * Practices to assign. OPTIONAL — the backend only iterates this list
   * (`StaffSetupService.cs:132`), so an empty one is accepted. The older
   * "at least one practice id must be passed" note was wrong, and the
   * practitioner create form depends on that: it defers practice assignment
   * to the Practices tab, like every other tab it defers.
   */
  practiceIds?: string[];
  /** Optional — most Add flows skip and let users assign specialities later. */
  specialities?: StaffSpeciality[];
}

/**
 * Compose the wire body for `POST /api/Staff` from user-facing inputs.
 *
 * Mirrors the `buildCreateDoctorBody` pattern shipped in PR 4 (Samer review
 * #323). Why a mapper:
 * - `staffSetupId: null` on create — backend allocates
 * - `assignStaffToPractice` is `{practiceId, isAssign:true}[]` — never just GUIDs
 * - `assignStaffToSpecialities` is a parallel `specialitySkillId[]` array;
 *   backend joins via both `specialities[]` AND `assignStaffToSpecialities[]`
 * - `photoURL` is empty / null on create (image upload is a separate flow)
 * - `isLastLogin: null`
 *
 * Wire-shape verified against working dev cURL 2026-05-13 (PROD-765
 * settings-side shipping work).
 */
export function buildCreateStaffBody(input: CreateStaffInput): Staff {
  const specialities = input.specialities ?? [];
  const practiceIds = input.practiceIds ?? [];
  return {
    // MUST be sent. A missing organizationId is NOT rejected: the backend
    // DTO's non-nullable Guid defaults to all-zeros and the staff member is
    // filed under the zero organisation. See the `Staff.organizationId` doc
    // for the full failure mode.
    organizationId: input.organizationId,
    providerId: null,
    staffSetupId: null,
    firstName: input.firstName,
    lastName: input.lastName,
    role: input.role,
    emailId: input.emailId,
    mobileNumber: input.mobileNumber,
    isActive: true,
    assignStaffToPractice: practiceIds.map<AssignStaffToPracticeRef>(
      (practiceId) => ({ practiceId, isAssign: true }),
    ),
    specialities,
    assignStaffToSpecialities: specialities.map((s) => s.specialitySkillId),
    isLastLogin: null,
    photoURL: null,
  };
}

/**
 * Create a staff member — POST /api/Staff (PROD-765 + PROD-927).
 *
 * Flutter parity: `createStaff` in `settings_data_source_impl.dart:846`.
 *
 * **Cache invalidation is NOT done in this hook** (Samer pattern, PR 4
 * #323 review). The caller controls when the staff list refetches —
 * typically AFTER any post-create UI (confirmation pane, follow-on sheet)
 * has unmounted, to avoid a flash of the underlying list re-rendering
 * while the sheet is still visible.
 *
 * Recommended caller pattern:
 *
 *   const create = useCreateStaff();
 *   const queryClient = useQueryClient();
 *   // ... on Sheet close (NOT in mutation onSuccess):
 *   queryClient.invalidateQueries({ queryKey: ['settings', 'staff-list'] });
 */
export function useCreateStaff() {
  return useMutation({
    mutationFn: (staff: Staff) => post<unknown>('/api/Staff', staff),
  });
}
